import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import { X } from "lucide-react";
import { initAnalytics, trackPageView } from "@/lib/analytics";

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const bannerRef = useRef<HTMLDivElement>(null);

  // Get language prefix for links
  const langPrefix = i18n.language === 'de' ? '/de' : i18n.language === 'nl' ? '/nl' : '';

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      setIsVisible(true);
    } else if (consent === "accepted") {
      // Initialize analytics for returning users who already accepted
      initAnalytics();
    }
  }, []);

  // Expose banner height as a CSS variable so other components can offset.
  useEffect(() => {
    if (!isVisible) {
      document.body.style.removeProperty("--cookie-banner-h");
      return;
    }
    const update = () => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      document.body.style.setProperty("--cookie-banner-h", `${h}px`);
    };
    update();
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      document.body.style.removeProperty("--cookie-banner-h");
    };
  }, [isVisible]);

  const handleAccept = () => {
    localStorage.setItem("cookie-consent", "accepted");
    setIsVisible(false);
    // Initialize analytics and track current page
    initAnalytics();
    trackPageView(location.pathname);
  };

  const handleReject = () => {
    localStorage.setItem("cookie-consent", "rejected");
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div
      ref={bannerRef}
      className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-lg pb-[env(safe-area-inset-bottom)]"
    >
      <button
        onClick={handleReject}
        aria-label={t('common:cookie.rejectAll')}
        className="md:hidden absolute right-2 top-2 p-1.5 text-muted-foreground hover:text-foreground"
      >
        <X className="w-4 h-4" />
      </button>
      <div className="container mx-auto px-4 py-3 md:py-4 md:pr-32">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex-1 pr-8 md:pr-0">
            <p className="text-xs md:text-sm text-foreground leading-snug line-clamp-2 md:line-clamp-none">
              {t('common:cookie.message')}{" "}
              <Link to={`${langPrefix}/privacy`} className="underline hover:text-fuego">
                {t('common:cookie.seePrivacy')}
              </Link>
              .
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReject}
              className="whitespace-nowrap flex-1 sm:flex-none hidden md:inline-flex"
            >
              {t('common:cookie.rejectAll')}
            </Button>
            <Button
              size="sm"
              onClick={handleAccept}
              className="whitespace-nowrap flex-1 sm:flex-none bg-fuego hover:bg-fuego/90 text-white"
            >
              {t('common:cookie.acceptAll')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
